import React from "react";
import { cn } from "@/lib/utils";

// Stage → token key (11 stage colour families, Component Sheet)
export const STAGE_KEY = {
  "New Lead": "new", "Document Collection": "docs", "Buyer Approval": "approval", "Logged-In": "loggedin",
  "IP Issued": "ip", "Legal & Technical": "legal", "Final Sanction": "final", "Part Disbursement": "part",
  "Disbursed": "disbursed", "Post Disbursement": "post", "Lost": "lost",
};
const STAGE_SHORT = { "Document Collection": "Doc Coll.", "Buyer Approval": "Buyer Appr.", "Legal & Technical": "Legal & Tech", "Part Disbursement": "Part Disb.", "Post Disbursement": "Post Disb." };

const chipBase = "inline-flex items-center gap-1 rounded-chip border font-display font-medium text-ui-2xs whitespace-nowrap transition-colors duration-120";
const sizes = { sm: "h-[18px] px-1.5", md: "h-[22px] px-2" };
// static class maps (Tailwind only generates classes it can see as literal strings)
const STAGE_CLS = {
  new: "text-stage-new bg-stage-new-bg border-stage-new-bd", docs: "text-stage-docs bg-stage-docs-bg border-stage-docs-bd",
  approval: "text-stage-approval bg-stage-approval-bg border-stage-approval-bd", loggedin: "text-stage-loggedin bg-stage-loggedin-bg border-stage-loggedin-bd",
  ip: "text-stage-ip bg-stage-ip-bg border-stage-ip-bd", legal: "text-stage-legal bg-stage-legal-bg border-stage-legal-bd",
  final: "text-stage-final bg-stage-final-bg border-stage-final-bd", part: "text-stage-part bg-stage-part-bg border-stage-part-bd",
  disbursed: "text-stage-disbursed bg-stage-disbursed-bg border-stage-disbursed-bd", post: "text-stage-post bg-stage-post-bg border-stage-post-bd",
  lost: "text-stage-lost bg-stage-lost-bg border-stage-lost-bd",
};
const STAGE_DOT = { new: "bg-stage-new-bd", docs: "bg-stage-docs-bd", approval: "bg-stage-approval-bd", loggedin: "bg-stage-loggedin-bd", ip: "bg-stage-ip-bd", legal: "bg-stage-legal-bd", final: "bg-stage-final-bd", part: "bg-stage-part-bd", disbursed: "bg-stage-disbursed-bg", post: "bg-stage-post-bd", lost: "bg-stage-lost-bd" };
const DOC_CLS = {
  verified: "text-doc-verified bg-doc-verified-bg border-doc-verified-bd", received: "text-doc-received bg-doc-received-bg border-doc-received-bd",
  reupload: "text-doc-reupload bg-doc-reupload-bg border-doc-reupload-bd", partial: "text-doc-partial bg-doc-partial-bg border-doc-partial-bd",
  missing: "text-doc-missing bg-doc-missing-bg border-doc-missing-bd border-dashed",
};
const DOC_DOT = { verified: "bg-doc-verified", received: "bg-doc-received", reupload: "bg-doc-reupload", partial: "bg-doc-partial", missing: "bg-doc-missing" };

export function StageChip({ stage, size = "md", interactive = false, short = false, onClick, className, testid }) {
  const k = STAGE_KEY[stage] || "new";
  const Tag = interactive ? "button" : "span";
  return (
    <Tag onClick={onClick} data-testid={testid || `stage-chip-${k}`}
      className={cn(chipBase, sizes[size], STAGE_CLS[k], interactive && "hover:brightness-95 cursor-pointer", className)}>
      {short ? STAGE_SHORT[stage] || stage : stage || "—"}{interactive && <span aria-hidden>▾</span>}
    </Tag>
  );
}

export function StageDot({ stage, className }) {
  const k = STAGE_KEY[stage] || "new";
  return <span title={stage} className={cn("inline-block w-2 h-2 rounded-full", STAGE_DOT[k], className)} />;
}

const DOC = { received: "Received", verified: "Verified", needsReupload: "Needs re-upload", reupload: "Needs re-upload", partial: "Partial", missing: "Missing" };
export function DocStatusChip({ status = "missing", size = "md", className }) {
  const k = status === "needsReupload" ? "reupload" : status;
  return (
    <span data-testid={`doc-chip-${k}`} className={cn(chipBase, sizes[size], DOC_CLS[k] || DOC_CLS.missing, className)}>
      <span className={cn("w-1.5 h-1.5 rounded-full", DOC_DOT[k] || DOC_DOT.missing)} /> {DOC[status] || status}
    </span>
  );
}

export function ConfidenceChip({ value, size = "md", className }) {
  const n = Number(value) || 0;
  const filled = n >= 90 ? 4 : n >= 75 ? 3 : n >= 50 ? 2 : n >= 25 ? 1 : 0;
  const notCounted = n > 0 && n < 50;
  return (
    <span data-testid={`confidence-chip-${n}`} title={notCounted ? `${n}% · tracked, not counted in the forecast` : `${n}% confidence`}
      className={cn(chipBase, sizes[size], "border-border bg-card", notCounted ? "text-muted-foreground" : "text-foreground", className)}>
      <span className="inline-flex gap-[2px]">
        {[0, 1, 2, 3].map((i) => <span key={i} className={cn("w-[5px] h-[9px] rounded-[1px]", i < filled ? (notCounted ? "v2-hatch" : "bg-success") : "bg-border")} />)}
      </span>
      {n ? `${n}%` : "—"}
    </span>
  );
}

export function ScopeChip({ value, size = "md", className }) {
  const label = value === "Yes" || value === "Truva" ? "Truva" : value === "No" || value === "Non Truva" || value === "Out of Truva" ? "Out of Truva" : null;
  if (!label) return null;
  return <span data-testid={`scope-chip-${label === "Truva" ? "truva" : "oot"}`} className={cn(chipBase, sizes[size], "border-border text-foreground bg-transparent", className)}>{label}</span>;
}

export function EscalationChip({ open = true, overdue8d = false, onClick, className }) {
  if (!open) return null;
  return (
    <button onClick={onClick} data-testid="escalation-chip" className={cn(chipBase, sizes.md, "border-destructive/30 bg-destructive/5 text-destructive hover:bg-destructive/10", className)}>
      <span className="w-1.5 h-1.5 rounded-full bg-destructive" /> Escalation open{overdue8d ? " · 8D overdue" : ""}
    </button>
  );
}
